import electrical from "@assets/icons/electrical.svg";
import network from "@assets/icons/network.svg";
import Card from "@components/global/new/Card/Card";
import Icon from "@components/global/new/Icon/Icon";
import RequestGetter from "@components/global/new/RequestGetter/RequestGetter";
import { MODALS_ENUM } from "@constants/Modals";
import { useEditorState3 } from "@mobx/hooks/useEditorState3";
import usePlaceID3 from "@mobx/hooks/usePlaceID";
import { EditorState3, toWorkEquipment3 } from "@mobx/store/connections";
import { AvailableEquipmentsList } from "@services/rackService";
import { setAvailableEquipment } from "@store/Equipments/actions";
import { EQUIPMENT_TYPE } from "@store/Equipments/actionTypes";
import cn from "classnames";
import { memo, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { show } from "redux-modal";
import Equipment3 from "../Equipment3";
import styles from "./AvailableEquipment.module.scss";

export interface IEquipmentTemplatesProps {
  readonly isFlipped: boolean;
}

type tabType = EQUIPMENT_TYPE;

const useControl = (
  props: IEquipmentTemplatesProps
) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<tabType>(EQUIPMENT_TYPE.ELECTRICAL);

  const { placeID } = usePlaceID3(true);
  
  const transform = (st?: EditorState3) => st?.data.nodes?.filter(node => node.type !== "consumer").map(node => toWorkEquipment3(node.data));
  const electrical = useEditorState3(EQUIPMENT_TYPE.ELECTRICAL, placeID, transform) ?? [];
  const networks = useEditorState3(EQUIPMENT_TYPE.NETWORK, placeID, transform) ?? [];

  const onOpenEquipmentModal = () => {
    dispatch(show(MODALS_ENUM.EQUIPMENT_MODAL, {
      showTypeSelector: true,
    }));
  };

  return {
    networks,
    electrical,
    activeTab,
    switchTab: setActiveTab,
    onOpenEquipmentModal,
  };
};

const EquipmentTemplates = (props: IEquipmentTemplatesProps) => {
  const { formatMessage } = useIntl();
  const control = useControl(props);

  return (
    <Card className={styles.controls}>
      {props.isFlipped && (
        <RequestGetter
          request={new AvailableEquipmentsList()}
          action={setAvailableEquipment}
        />
      )}
      <div className={styles.cardHead}>
        {formatMessage({ id: "location.available_equipment.title" })}
      </div>
      <div className={styles.cardContent}>
        <div className={styles.tabs}>
          <button
            className={cn(styles.tab, { [styles.active]: control.activeTab === EQUIPMENT_TYPE.NETWORK })}
            onClick={() => control.switchTab(EQUIPMENT_TYPE.NETWORK)}
          >
            <span className={styles.icon}><Icon src={network} /></span>
            {formatMessage({ id: "location.available_equipment.network" })}
          </button>
          <button
            className={cn(styles.tab, { [styles.active]: control.activeTab === EQUIPMENT_TYPE.ELECTRICAL })}
            onClick={() => control.switchTab(EQUIPMENT_TYPE.ELECTRICAL)}
          >
            <span className={styles.icon}><Icon src={electrical} /></span>
            {formatMessage({ id: "location.available_equipment.electrical" })}
          </button>
        </div>
        <div className={styles.controls_sections}>
          <div className={cn(styles.section, { [styles.hidden]: control.activeTab !== EQUIPMENT_TYPE.NETWORK })}>
            {control.networks.map((n, i) => (
              <Equipment3 eq={n} key={n.id} last={i === control.networks.length - 1} />
            ))}
          </div>
          <div className={cn(styles.section, { [styles.hidden]: control.activeTab !== EQUIPMENT_TYPE.ELECTRICAL })}>
            {control.electrical.map((n, i) => (
              <Equipment3 eq={n} key={n.id} last={i === control.electrical.length - 1} />
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default memo(EquipmentTemplates);
